import { useState } from "react";
import { useTranslation } from "react-i18next";
import ToolShell, { Panel, FieldLabel, Choice } from "@/components/tools/ToolShell";
import { ROADMAP_PHASES, TOOL_PATHS, buildRoadmap } from "@/config/tools";

function Question({ label, options, value, onChange, testid }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className={`grid gap-3 ${options.length > 2 ? "" : "grid-cols-2"}`}>
        {options.map(([v, text]) => (
          <Choice key={v} active={value === v} onClick={() => onChange(v)} testid={`${testid}-${v}`}>{text}</Choice>
        ))}
      </div>
    </div>
  );
}

export default function RelocationRoadmap() {
  const { t } = useTranslation();
  const [a, setA] = useState({ citizenship: "non_eu", route: "employment", family: "alone", rule: "d60", hasCompany: false });
  const set = (k) => (v) => setA((x) => ({ ...x, [k]: v }));
  const { steps, total } = buildRoadmap(a);
  const r = (k) => t(`tools.roadmap.${k}`);

  return (
    <ToolShell id="roadmap" path={TOOL_PATHS.roadmap}>
      <div className="grid gap-px bg-[var(--line)] lg:grid-cols-12">
        <Panel className="lg:col-span-4 bg-[var(--ink)] border-0 space-y-8">
          <Question label={r("citizenship")} value={a.citizenship} onChange={set("citizenship")} testid="roadmap-citizenship"
            options={[["eu", r("eu")], ["non_eu", r("nonEu")]]} />
          {a.citizenship === "non_eu" && (
            <Question label={r("route")} value={a.route} onChange={set("route")} testid="roadmap-route"
              options={["employment", "nomad", "investment"].map((k) => [k, t(`tools.roadmap.routes.${k}`)])} />
          )}
          <Question label={r("family")} value={a.family} onChange={set("family")} testid="roadmap-family"
            options={[["alone", r("alone")], ["family", r("withFamily")]]} />
          <Question label={r("rule")} value={a.rule} onChange={set("rule")} testid="roadmap-rule"
            options={[["d60", r("d60")], ["d183", r("d183")]]} />
          <Question label={r("hasCompany")} value={a.hasCompany} onChange={set("hasCompany")} testid="roadmap-company"
            options={[[true, r("yes")], [false, r("no")]]} />
        </Panel>
        <Panel className="lg:col-span-8 bg-[var(--ink-2)] border-0" data-testid="roadmap-results">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">{r("total")}</div>
              <div className="display text-4xl md:text-5xl text-[var(--bone)] mt-2" data-testid="roadmap-total">{t("tools.roadmap.weeks", { count: total })}</div>
            </div>
          </div>
          {ROADMAP_PHASES.map((phase) => {
            const ps = steps.filter((s) => s.phase === phase);
            if (!ps.length) return null;
            return (
              <div key={phase} className="mb-8" data-testid={`roadmap-phase-${phase}`}>
                <div className="overline mb-3">{t(`tools.roadmap.phases.${phase}`)}</div>
                <ul className="divide-y divide-[var(--line)] border-t border-[var(--line)]">
                  {ps.map((s) => (
                    <li key={s.id} className="py-4" data-testid={`roadmap-step-${s.id}`}>
                      <div className="flex items-baseline justify-between gap-4 flex-wrap">
                        <span className="text-[var(--bone)]">{t(`tools.roadmap.steps.${s.id}.title`)}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--mute)]">
                          {t("tools.roadmap.weekRange", { start: s.start + 1, end: s.end })}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--mute)] mt-1">{t(`tools.roadmap.steps.${s.id}.desc`)}</p>
                      <div className="relative h-[3px] mt-3 bg-[var(--line)]" aria-hidden="true">
                        <span className="absolute top-0 h-full bg-[var(--pewter)]"
                          style={{ insetInlineStart: `${(s.start / total) * 100}%`, width: `${((s.end - s.start) / total) * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </Panel>
      </div>
    </ToolShell>
  );
}
